import { Link } from 'react-router-dom';
import { CheckCircle2, ShieldCheck, PawPrint, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Etiquette = () => {
  return (
    <div className="min-h-screen">
      {/* Hero — tight, premium */}
      <section className="relative overflow-hidden bg-gradient-to-br from-rose-50 via-amber-50/40 to-cyan-50 py-10 md:py-14">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-3 bg-cyan-100 text-cyan-800 hover:bg-cyan-200 border-cyan-200">
              Event Guidelines
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3 tracking-tight">
              Event Etiquette
            </h1>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Help us create a safe, respectful, and enjoyable experience for all attendees — both two-legged and four-legged.
            </p>
          </div>
        </div>
      </section>

      {/* Welcome to Our Dog Day Out */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center bg-gradient-to-br from-amber-400 to-rose-500 text-white w-16 h-16 rounded-2xl shadow-lg mb-5">
                <PawPrint className="h-8 w-8" />
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
                Welcome to Our Dog Day Out!
              </h2>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
                We are so excited to have you join us for a fun-filled day with our furry friends.
                To help everyone — humans and dogs alike — have a safe and enjoyable time, please
                take a moment to read through our event etiquette and safety guidelines:
              </p>
            </div>

            {/* Guideline cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: 'Water Safety Comes First', body: 'This event is located on the water — please keep an eye on your children and furbabies at all times for safety purposes!' },
                { title: 'Keep Dogs Leashed at All Times', body: 'This is a Council requirement and fines apply for off-leash dogs.' },
                { title: 'Calm & Manageable Dogs', body: 'Please ensure your dog remains calm and manageable throughout the event, for the safety and enjoyment of all guests.' },
                { title: 'Avoid Retractable Leashes', body: 'These can be unsafe in crowded environments.' },
                { title: 'Respect the Performance Arena', body: 'Stay at least 1 metre away from the arena barrier.' },
                { title: 'No Touching the Barrier', body: "It's for visual guidance only and not designed to support weight. Please help children understand the importance of keeping their distance." },
                { title: 'Clean Up After Your Dog', body: 'Bring poo bags — if you run out, visit the TWRA marquee for more.' },
                { title: 'Dispose of Waste Properly', body: "Let's keep the park beautiful — please take all your litter with you or use the bins around the area." },
                { title: 'Getting Here', body: 'Minimal residential parking at Warwick Daniels Lake View Park — we encourage walking or using the drop-off/pick-up zones around the park.' },
                { title: 'In Case of Emergency', body: "A Paramedic will be on site near the TWRA membership table. Don't hesitate to ask for help if needed." },
              ].map((item) => (
                <div
                  key={item.title}
                  className="group flex items-start gap-4 p-5 bg-white rounded-xl border border-slate-200 hover:border-cyan-300 hover:shadow-md transition-all"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-50 to-teal-50 border border-cyan-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <CheckCircle2 className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900 mb-1 leading-tight">{item.title}</p>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 p-6 bg-gradient-to-r from-cyan-50 via-teal-50 to-emerald-50 border border-cyan-100 rounded-2xl flex items-start gap-4">
              <Heart className="h-6 w-6 text-rose-500 flex-shrink-0 mt-1" />
              <p className="text-base md:text-lg text-slate-800 leading-relaxed">
                Thanks for being a responsible and caring member of our dog-loving community.{' '}
                <span className="font-semibold text-cyan-700">Let's have a tail-wagging good time!</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Introducing The Traffic Light Ribbon System */}
      <section className="py-12 md:py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Header ABOVE the image */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center bg-gradient-to-br from-cyan-600 to-teal-600 text-white w-16 h-16 rounded-2xl shadow-lg mb-5">
                <ShieldCheck className="h-8 w-8" />
              </div>
              <Badge className="mb-3 bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-200">
                New This Year
              </Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
                Introducing The Traffic Light Ribbon System
              </h2>
              <p className="text-base md:text-lg text-slate-600 max-w-3xl mx-auto">
                We use a simple colour-coded ribbon system to help everyone understand how to interact with each dog.
                Ribbons will be distributed at various locations and by people roaming through the event.
              </p>
            </div>

            {/* Ribbon image */}
            <div className="max-w-3xl mx-auto mb-10 rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-white">
              <img
                src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/oi8z3jxh_Screenshot%202026-05-17%20at%2011.06.27%E2%80%AFam.png"
                alt="Traffic Light Ribbon System — Red, Yellow and Green ribbons explaining how to approach each dog"
                className="w-full h-auto"
              />
            </div>

            {/* Ribbon explanation cards */}
            <div className="grid md:grid-cols-3 gap-5">
              <Card className="border-red-200 bg-gradient-to-br from-red-50 to-white hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-9 h-9 bg-red-500 rounded-full shadow-md ring-4 ring-red-100"></div>
                    <CardTitle className="text-lg text-red-900">Red: Stay Away</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    <strong>Do not approach</strong> — this dog requires space and distance. They may be nervous,
                    in training, or need their personal space respected.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-yellow-200 bg-gradient-to-br from-yellow-50 to-white hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-9 h-9 bg-yellow-500 rounded-full shadow-md ring-4 ring-yellow-100"></div>
                    <CardTitle className="text-lg text-yellow-900">Yellow: Cautious</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Approach slowly and <strong>ask the owner first</strong>. This dog may be shy, anxious, or still learning social skills.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-9 h-9 bg-green-500 rounded-full shadow-md ring-4 ring-green-100"></div>
                    <CardTitle className="text-lg text-green-900">Green: Friendly</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    <strong>Approachable and friendly</strong> to humans and other dogs. Feel free to say hello, but always be gentle and respectful.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Thank You Section */}
      <section className="py-12 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 tracking-tight">
            Thank You for Your Cooperation!
          </h2>
          <p className="text-base md:text-lg mb-6 text-cyan-50 max-w-2xl mx-auto">
            By following these guidelines, you're helping us create a wonderful, safe experience for everyone at Paws in the Park 2026.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild size="default" variant="secondary" className="bg-white text-cyan-700 hover:bg-slate-100">
              <Link to="/">Back to Home</Link>
            </Button>
            <Button asChild size="default" variant="outline" className="border-white text-white hover:bg-white/10">
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Etiquette;
